import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2 } from "lucide-react";
import { GALLERY_CATEGORIES } from "../../content/site";
import { http, apiError, galleryFileUrl } from "../../lib/api";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const CATS = GALLERY_CATEGORIES.filter((c) => c.id !== "all");

export const GalleryPanel = () => {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("wedding");
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showSamples, setShowSamples] = useState(true);
  const fileRef = useRef(null);

  useEffect(() => {
    http.get("/gallery").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    http.get("/settings").then((r) => setShowSamples(r.data?.show_sample_gallery ?? true)).catch(() => {});
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    const files = Array.from(fileRef.current?.files || []);
    if (!files.length) return toast.error("Choose at least one image");
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", category);
        fd.append("caption", caption);
        const { data } = await http.post("/gallery", fd);
        setItems((xs) => [data, ...xs]);
      }
      toast.success(`${files.length} photo(s) uploaded`);
      fileRef.current.value = "";
      setCaption("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await http.delete(`/gallery/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggleSamples = async (v) => {
    setShowSamples(v);
    try { await http.put("/settings", { show_sample_gallery: v }); toast.success(v ? "Sample photos shown" : "Sample photos hidden"); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="gallery-panel">
      <form onSubmit={upload} className="grid gap-4 rounded-2xl border border-gold/20 bg-white p-5 md:grid-cols-4 md:items-end">
        <label className="md:col-span-2"><span className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Images</span>
          <Input ref={fileRef} type="file" accept="image/*" multiple className="mt-1 h-11 rounded-xl" data-testid="gallery-file-input" /></label>
        <label><span className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Category</span>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="gallery-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
          </Select></label>
        <label><span className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Caption</span>
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Optional" className="mt-1 h-11 rounded-xl" data-testid="gallery-caption-input" /></label>
        <button type="submit" disabled={uploading} className="btn-gold md:col-span-4" data-testid="gallery-upload-btn">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload to gallery
        </button>
      </form>
      <div className="mt-5 flex items-center gap-3 rounded-2xl border border-gold/20 bg-white p-4">
        <Switch checked={showSamples} onCheckedChange={toggleSamples} data-testid="gallery-samples-switch" />
        <span className="text-sm text-ink">Show sample placeholder photos on the website</span>
      </div>
      <p className="mt-8 font-serif text-2xl text-ink">Your uploads ({items.length})</p>
      {items.length === 0 && <p className="mt-4 rounded-2xl border border-dashed border-gold/30 p-10 text-center text-ink-soft" data-testid="gallery-empty">No uploads yet. Sample photos are shown on the site.</p>}
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((i) => (
          <div key={i.id} className="group relative overflow-hidden rounded-2xl border border-gold/20 bg-white" data-testid={`gallery-admin-item-${i.id}`}>
            <img src={galleryFileUrl(i.id)} alt={i.caption || i.category} loading="lazy" className="aspect-square w-full object-cover" />
            <div className="flex items-center justify-between p-2.5 text-xs">
              <span className="capitalize text-ink">{i.category}{i.caption ? ` · ${i.caption}` : ""}</span>
              <button onClick={() => remove(i.id)} className="text-red-700" aria-label="Delete photo" data-testid={`gallery-delete-${i.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
